import styled from 'styled-components';
import { EntityOffer, Offer } from '../../../../new-lib/types/entity';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { getPlanDuration } from '../../../../helpers/payment-plan';

const CompoundOfferPlan = ({ offer }: { offer: Offer }) => {
  const { locale } = useRouter();
  const { t } = useTranslation(['entity', 'common']);

  const getCardContent = () => {
    if (offer?.cashDiscountPercentage) {
      return (
        <div className="offer-content">
          <div className="percentage-payment">
            <span>{Math.round(offer.cashDiscountPercentage)}%</span>
            <span>{t('cash_discount')}</span>
          </div>
        </div>
      );
    }

    if (offer?.downPaymentPercentage >= 0) {
      return (
        <h3 className="offer-content">
          <div className="percentage-payment">
            <span>{offer.downPaymentPercentage}% </span>
            <span>{t('downpayment')} </span>
          </div>
          <div className="installment-years">
            {offer.years && offer.years > 0 && (
              <span>
                {offer.years} {t('years')}
              </span>
            )}
          </div>
        </h3>
      );
    }
    return (
      <h3 className="offer-content discount">
        <div className="percentage-payment">
          <span>{Math.round(offer.discountPercentage)}% </span>
          <span className="discount-text">{t('discount')} </span>
        </div>
        <div className="installment-years discount">
          <span>
            {offer?.originalDownPaymentPercentage && offer?.originalYears ? (
              <span>
                {t('valid_on')} {offer.originalDownPaymentPercentage}%{' '}
                {t('downpayment')}
                {', '}
                {offer.originalYears} {t('years')}
              </span>
            ) : (
              <span className="offer-description">{offer.description}</span>
            )}
          </span>
        </div>
      </h3>
    );
  };

  return (
    <OfferCard locale={locale}>
      <div className="offer-tag">
        <Image
          height={47}
          width={53}
          alt="Offer Tag"
          src={`/assets/icons/entity/offer-tag-new.svg`}
        />
      </div>
      {getCardContent()}
      {offer.isBackLoaded && (
        <div className="escalating-section">
          <div className="separator" />
          <div className="plan-type">
            {t('escalating_payments', { ns: 'common' })}
          </div>
        </div>
      )}
    </OfferCard>
  );
};

const OfferCard = styled.div<any>`
  position: relative;
  height: 60px;
  min-width: 368px;
  max-width: 368px;
  padding: 12px;
  border-radius: 8px;
  background: #f7f9fd;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  .separator {
    width: 1px;
    height: 36px;
    background: #d3dbe6;
  }

  .offer-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    text-align: center;

    &.discount {
      flex-direction: column;
      align-items: flex-start;
      gap: 0px;
    }

    .percentage-payment {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 6px;

      .discount-text {
        margin-bottom: 3px;
        ${props => props.locale == 'en' && 'align-self: flex-end'};
      }

      span {
        &:first-child {
          font-family: materialBold;
          color: #ff9108;
          font-size: 24px;
        }

        &:last-child {
          width: 55px;
          text-align: start;
          color: #ff9108;
          font-family: materialRegular;
          font-size: 12px;
        }
      }
    }
  }

  .installment-years {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: #ff9108;
    text-align: center;
    font-family: materialMedium;
    font-size: 12px;
  }

  .description {
    color: #666;
    text-align: center;
    font-family: materialMedium;
    font-size: 12px;

    &.bold {
      font-family: materialBold;
    }
  }

  .offer-tag {
    position: absolute;
    top: 0px;
    inset-inline-end: 0px;

    ${props => props.locale === 'ar' && 'transform: scaleX(-1)'};
  }

  .escalating-section {
    display: flex;
    gap: 12px;
    max-width: 35%;
  }

  .plan-type {
    font-family: materialMedium;
    font-size: 11px;
    color: #3e4042;
    width: 30%;
    display: flex;
    align-items: center;
    gap: 8px;

    .escalating-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      background: #edf1f6;
      padding: 4px;
    }
  }
`;

export default CompoundOfferPlan;
